// Implements: .claude/skills/rehaut-design/ui_kits/rehaut-signin/signin.jsx
// See: .claude/skills/rehaut-design/HANDOFF.md
//
// Single-step v2 sign-in. Production's auth API takes
// `username@clinicslug` + password in a single shot, so we keep one
// form (the kit's email → password → MFA stepper would need new
// account-discovery and MFA infra).
//
// 2-pane on desktop, form-only on tablet + mobile. The kit's
// brand-side panel is cosmetic (quote + constellation backdrop +
// compliance footer) — no auth-infra dependency, so it ships even
// though the multi-step flow doesn't.
'use client';

import { Suspense, useState, useCallback, useEffect, useMemo, type CSSProperties, type ReactElement } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Icon } from '@/components/system';
import { useBreakpoint } from '@/lib/utils';

/** Same validation as src/lib/session.ts isSafeNextPath, inlined
 *  here because session.ts touches process.env.SESSION_SECRET and
 *  this is a 'use client' file — keeping it self-contained avoids
 *  bundling server-only code into the client. */
function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null;
  if (raw === '/') return null;
  return raw;
}

export default function LoginPage() {
  // Suspense wraps the content so useSearchParams() can prerender —
  // Next.js fails the build otherwise (a hard error since 14.x).
  // Fallback matches the in-flight auth-check screen so the build
  // shell + client transition look identical.
  return (
    <Suspense
      fallback={
        <div style={{ minHeight: '100vh', background: 'var(--bg)' }} />
      }
    >
      <LoginPageContent />
    </Suspense>
  );
}

function LoginPageContent() {
  const router = useRouter();
  const bp = useBreakpoint();
  const showBrand = bp === 'desktop';
  const searchParams = useSearchParams();
  // Resolved redirect target after login. Falls back to /home when
  // no ?next= is present or when the supplied path fails the safety
  // check. Memoized so it's stable across renders even if other
  // params change.
  const nextTarget = useMemo(
    () => safeNextPath(searchParams.get('next')) ?? '/home',
    [searchParams],
  );

  // Pre-render auth check — if the user is already logged in, push
  // them to nextTarget without ever painting the sign-in surface.
  // Lifted from FormSide so the BrandSide flash also gets gated;
  // otherwise an authed user with the page open in a tab would see
  // the full split-pane briefly before redirect.
  const [authChecked, setAuthChecked] = useState(false);
  useEffect(() => {
    fetch('/api/auth')
      .then((r) => r.json())
      .then((d) => {
        if (d?.user) {
          router.push(nextTarget);
          return;
        }
        setAuthChecked(true);
      })
      .catch(() => setAuthChecked(true));
  }, [router, nextTarget]);

  if (!authChecked) {
    // Solid bg matching the page so the ~50-200ms check window
    // doesn't show the form to users who are about to redirect.
    // No spinner — at this duration a spinner would flash for
    // less than a frame and feel worse than blank.
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--bg)',
        }}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: showBrand ? '1fr 1fr' : '1fr',
        background: 'var(--bg)',
        color: 'var(--ink)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <FormSide nextTarget={nextTarget} />
      {showBrand && <BrandSide />}
    </div>
  );
}

function FormSide({ nextTarget }: { nextTarget: string }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ username?: string; password?: string }>({});
  const [showPwd, setShowPwd] = useState(false);
  const [recent, setRecent] = useState<string[]>([]);

  // Auth check + auto-redirect lives in the LoginPage parent, so
  // FormSide doesn't need to repeat it. Mounting FormSide is itself
  // the proof that the user is unauthenticated.

  // Recent accounts — read from localStorage on mount. Mirrors the
  // kit's "Cuentas recientes en este equipo" affordance for shared
  // clinic devices. Privacy footprint is mild (anyone on the device
  // sees previous usernames), but cohabiting clinic teams already
  // share the same physical workstation.
  useEffect(() => {
    setRecent(loadRecent());
  }, []);

  const pickRecent = useCallback((u: string) => {
    setUsername(u);
    setError('');
    setFieldErrors({});
    // Focus the password field so the user can keep typing.
    setTimeout(() => document.getElementById('pw')?.focus(), 0);
  }, []);

  const handleRemoveRecent = useCallback((u: string) => {
    removeRecent(u);
    setRecent((prev) => prev.filter((x) => x !== u));
  }, []);

  const handleSubmit = useCallback(async () => {
    const errs: { username?: string; password?: string } = {};
    if (!username.trim()) errs.username = 'Usuario requerido';
    if (!password) errs.password = 'Contraseña requerida';
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json();
      if (res.ok && data.user) {
        // Persist to recent-accounts list before the redirect — the
        // hard-nav clears component state, so this has to happen
        // synchronously here, not in a follow-up effect.
        saveRecent(username.trim());
        // Hard nav so cookies are picked up by the next request.
        window.location.href = nextTarget;
      } else {
        setError(data.error || 'Usuario o contraseña incorrectos');
        setLoading(false);
      }
    } catch {
      setError('Error de conexión');
      setLoading(false);
    }
  }, [username, password]);

  const canSubmit = username.trim().length > 0 && password.length > 0 && !loading;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg)',
      }}
    >
      {/* Corner mark — mirrors the kit's FormSide header. */}
      <header
        style={{
          padding: '24px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '2px',
          }}
        >
          RÉHAUT
        </div>
        {/* TODO(multi-tenant): "by Juvenalia Brío" is hardcoded. When
            clinic #2 onboards, derive from URL subdomain (`condesa.rehaut.mx`)
            or from the username field's clinic-slug half. Decide before
            second-clinic GA. */}
        <div style={{ fontSize: '11px', color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
          by Juvenalia Brío
        </div>
      </header>

      {/* Centered form card */}
      <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: '0 32px' }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          style={{
            width: '100%',
            maxWidth: '380px',
            animation: 'signin-fade-up 280ms var(--ease-quiet) both',
          }}
        >
          <Heading
            eyebrow="ACCESO"
            title="Inicia sesión en Réhaut."
            sub="Usa tu usuario de clínica para continuar."
          />

          <FieldShell label="Usuario" hint="Ej: usuario@clinica">
            <input
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setError('');
                setFieldErrors({});
              }}
              onFocus={() => setFocused('user')}
              onBlur={() => setFocused(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  document.getElementById('pw')?.focus();
                }
              }}
              placeholder="usuario@clinica"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              autoFocus
              style={inputStyle(focused === 'user', !!fieldErrors.username)}
            />
            {fieldErrors.username && <ErrorLine text={fieldErrors.username} />}
          </FieldShell>

          <div style={{ height: '14px' }} />

          <FieldShell label="Contraseña">
            <div style={{ position: 'relative' }}>
              <input
                id="pw"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                  setFieldErrors({});
                }}
                onFocus={() => setFocused('pass')}
                onBlur={() => setFocused(null)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder="••••••••"
                autoComplete="current-password"
                style={{ ...inputStyle(focused === 'pass', !!fieldErrors.password), paddingRight: '64px' }}
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '11px',
                  color: 'var(--ink-3)',
                  padding: '4px 8px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 'var(--r-sm)',
                  fontFamily: 'var(--font-sans)',
                  cursor: 'pointer',
                }}
              >
                {showPwd ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            {fieldErrors.password && <ErrorLine text={fieldErrors.password} />}
          </FieldShell>

          {error && <ErrorLine text={error} />}

          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              width: '100%',
              marginTop: '16px',
              // Min 44px hit area on touch — was 38px which fails WCAG 2.5.5.
              minHeight: '44px',
              padding: '12px 14px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#fff',
              background: canSubmit ? 'var(--accent)' : 'var(--ink-5)',
              border: '1px solid transparent',
              borderRadius: 'var(--r-md)',
              fontFamily: 'var(--font-sans)',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background 120ms var(--ease-quiet), opacity 120ms var(--ease-quiet)',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? (
              'Verificando…'
            ) : (
              <>
                Iniciar sesión <Icon name="arrow_r" size={11} color="#fff" />
              </>
            )}
          </button>

          {recent.length > 0 && (
            <RecentAccounts
              items={recent}
              active={username}
              onPick={pickRecent}
              onRemove={handleRemoveRecent}
            />
          )}
        </form>
      </div>

      <footer
        style={{
          padding: '20px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '11px',
          color: 'var(--ink-4)',
          borderTop: '1px solid var(--hairline)',
        }}
      >
        {/* TODO(multi-tenant): "La Condesa · Ciudad de México" is
            hardcoded. Same fix as the corner mark — derive from
            subdomain or clinic-slug half of the username. Decide
            before second-clinic GA; until then this lies for any
            future tenant. */}
        <span style={{ fontFamily: 'var(--font-mono)' }}>La Condesa · Ciudad de México</span>
        <span>ES · MX</span>
      </footer>
    </div>
  );
}

// Right pane — cosmetic-only. Quote + constellation backdrop +
// compliance footer. Hidden on tablet and mobile.
function BrandSide() {
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        borderLeft: '1px solid var(--hairline)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <BrandTexture />

      {/* Center quote */}
      <div
        style={{
          flex: 1,
          display: 'grid',
          placeItems: 'center',
          padding: '0 64px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div style={{ maxWidth: 460 }}>
          {/* TODO(multi-tenant): "La Condesa" hardcoded — same fix
              as the FormSide corner mark + footer. Derive from
              subdomain or username clinic-slug. */}
          <div
            style={{
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              letterSpacing: '1.4px',
              textTransform: 'uppercase',
              color: 'var(--ink-4)',
              marginBottom: 24,
            }}
          >
            Réhaut · v2 · La Condesa
          </div>
          <h2
            style={{
              fontSize: 36,
              fontWeight: 300,
              letterSpacing: '-0.02em',
              color: 'var(--ink)',
              lineHeight: 1.15,
              margin: 0,
            }}
          >
            Diseñado para integrarse a tu día a día.{' '}
            <span style={{ color: 'var(--accent-ink)', fontStyle: 'italic', fontWeight: 400 }}>
              Para que tu clínica brille.
            </span>
          </h2>
        </div>
      </div>

      <BrandFooter />
    </div>
  );
}

// Sparse dot constellation + two warm orbits, all very low opacity.
// Lifted from the kit's BrandTexture; sized to viewBox 672×864 and
// stretched to fill the pane via preserveAspectRatio.
function BrandTexture() {
  const dots: ReactElement[] = [];
  const cols = 14;
  const rows = 18;
  const cx = (cols - 1) / 2;
  const cy = (rows - 1) / 2;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const dist = Math.hypot(x - cx, y - cy);
      const op = Math.max(0.02, 0.1 - dist * 0.012);
      dots.push(
        <circle
          key={`${x}-${y}`}
          cx={x * 48 + 24}
          cy={y * 48 + 24}
          r="1.2"
          fill="var(--ink)"
          opacity={op}
        />,
      );
    }
  }
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 672 864"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
      style={{ position: 'absolute', inset: 0 }}
    >
      {dots}
      {/* Two warm orbits — Réhaut signature decoration. */}
      <circle
        cx="336"
        cy="430"
        r="280"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="1"
        strokeDasharray="2 6"
        opacity="0.18"
      />
      <circle
        cx="336"
        cy="430"
        r="200"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="1"
        strokeDasharray="2 6"
        opacity="0.10"
      />
    </svg>
  );
}

function BrandFooter() {
  return (
    <div
      style={{
        padding: '12px 40px',
        borderTop: '1px solid var(--hairline)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 11,
        color: 'var(--ink-4)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <span style={{ fontFamily: 'var(--font-mono)' }}>
        SOC2 Type II · ISO 27001 · LFPDPPP
      </span>
      <span>Hecho en CDMX</span>
    </div>
  );
}

function Heading({ eyebrow, title, sub }: { eyebrow: string; title: string; sub: string }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '10.5px',
          letterSpacing: '1.4px',
          textTransform: 'uppercase',
          color: 'var(--ink-4)',
          marginBottom: '6px',
          fontWeight: 500,
        }}
      >
        {eyebrow}
      </div>
      <h1
        style={{
          fontSize: '20px',
          fontWeight: 500,
          letterSpacing: '-0.01em',
          color: 'var(--ink)',
          margin: 0,
        }}
      >
        {title}
      </h1>
      <div style={{ fontSize: '13px', color: 'var(--ink-3)', marginTop: '4px' }}>{sub}</div>
    </div>
  );
}

function FieldShell({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'block' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: '6px',
        }}
      >
        <span
          style={{
            fontSize: '10.5px',
            fontWeight: 500,
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            color: 'var(--ink-4)',
          }}
        >
          {label}
        </span>
        {hint && <span style={{ fontSize: '11px', color: 'var(--ink-4)' }}>{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function ErrorLine({ text }: { text: string }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: '8px',
        fontSize: '12px',
        color: 'var(--error)',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'var(--error)' }} />
      {text}
    </div>
  );
}

// ── Recent accounts (localStorage-backed) ──────────────────────

const RECENT_KEY = 'rehaut.recent_logins';
const MAX_RECENT = 3;

function loadRecent(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x): x is string => typeof x === 'string' && x.length > 0)
      .slice(0, MAX_RECENT);
  } catch {
    // Malformed JSON, blocked storage, etc. — treat as empty.
    return [];
  }
}

function saveRecent(usernameToSave: string) {
  if (typeof window === 'undefined' || !usernameToSave) return;
  try {
    const current = loadRecent();
    const next = [usernameToSave, ...current.filter((u) => u !== usernameToSave)].slice(
      0,
      MAX_RECENT,
    );
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage write blocked or full — fail silent.
  }
}

function removeRecent(usernameToRemove: string) {
  if (typeof window === 'undefined' || !usernameToRemove) return;
  try {
    const current = loadRecent();
    const next = current.filter((u) => u !== usernameToRemove);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage write blocked — fail silent.
  }
}

function RecentAccounts({
  items,
  active,
  onPick,
  onRemove,
}: {
  items: string[];
  active: string;
  onPick: (u: string) => void;
  onRemove: (u: string) => void;
}) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  return (
    <div style={{ marginTop: '32px' }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '10.5px',
          letterSpacing: '1.4px',
          textTransform: 'uppercase',
          color: 'var(--ink-4)',
          marginBottom: '10px',
          fontWeight: 500,
        }}
      >
        Cuentas recientes en este equipo
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          background: 'var(--hairline)',
          border: '1px solid var(--hairline)',
          borderRadius: 'var(--r-md)',
          overflow: 'hidden',
        }}
      >
        {items.map((u, i) => {
          const isActive = u === active;
          const isHovered = hoveredIdx === i;
          return (
            <div
              key={u}
              style={{ position: 'relative' }}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              <button
                type="button"
                onClick={() => onPick(u)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '28px 1fr',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  padding: '10px 36px 10px 12px',
                  background: isActive
                    ? 'var(--accent-bg)'
                    : isHovered
                      ? 'var(--surface-2)'
                      : 'var(--surface)',
                  textAlign: 'left',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  transition: 'background 120ms var(--ease-quiet)',
                }}
              >
                <div
                  aria-hidden
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--hairline)',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 10.5,
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--ink-2)',
                  }}
                >
                  {initialsFromUsername(u)}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--ink)',
                    fontFamily: 'var(--font-mono)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {u}
                </div>
              </button>
              {/* × button is a sibling of the row button (not a child),
                  so DOM-wise no event bubbles to the row's onClick. */}
              {isHovered && (
                <button
                  type="button"
                  aria-label={`Quitar ${u}`}
                  onClick={() => onRemove(u)}
                  style={{
                    position: 'absolute',
                    right: 6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 24,
                    height: 24,
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--ink-4)',
                    cursor: 'pointer',
                    fontSize: 14,
                    lineHeight: 1,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 'var(--r-sm)',
                    fontFamily: 'var(--font-sans)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(20,20,19,0.06)';
                    e.currentTarget.style.color = 'var(--ink-2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--ink-4)';
                  }}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Two uppercase chars from the username's local part. Splits on
 *  '.', '-', '_', or '@' so "j.voss" → "JV", "marisol" → "MA",
 *  "ines.arce@condesa" → "IA". */
function initialsFromUsername(u: string): string {
  const local = u.split('@')[0] || u;
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length === 0) return u.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function inputStyle(focusedNow: boolean, hasError: boolean): CSSProperties {
  return {
    width: '100%',
    // 12px vertical padding × 16px font ≈ 44px hit area, also defeats
    // iOS Safari's auto-zoom on focus (triggers below 16px font).
    padding: '12px 14px',
    background: 'var(--surface)',
    border: `1px solid ${
      hasError
        ? 'var(--error)'
        : focusedNow
          ? 'var(--accent)'
          : 'var(--hairline-strong)'
    }`,
    boxShadow: focusedNow ? '0 0 0 3px var(--accent-bg)' : 'none',
    borderRadius: 'var(--r-md)',
    color: 'var(--ink)',
    fontSize: '16px',
    fontFamily: 'var(--font-sans)',
    outline: 'none',
    transition: 'border-color 120ms var(--ease-quiet), box-shadow 120ms var(--ease-quiet)',
    boxSizing: 'border-box',
  };
}
